<template>
  <div class="tw-flex tw-gap-2">
    <div class="tw-relative tw-flex-1">
      <!-- Currency Input -->
      <FancyCurrencyInput
        v-if="useCurrency"
        v-model="startCurrencyValue"
        placeholder="Min"
        class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
        @update:model-value="handleStartCurrencyChange"
      />
      <!-- Regular Number Input -->
      <input
        v-else
        type="number"
        v-model="startValue"
        @input="handleNumberRangeFilter"
        @click.stop
        placeholder="Min"
        class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
      />
      <button
        v-if="startValue !== undefined && startValue !== null"
        @click.stop="clearStartValue"
        type="button"
        class="tw-absolute tw-right-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
        title="Clear minimum value"
      >
        <svg class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>
    <div class="tw-relative tw-flex-1">
      <!-- Currency Input -->
      <FancyCurrencyInput
        v-if="useCurrency"
        v-model="endCurrencyValue"
        placeholder="Max"
        class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
        @update:model-value="handleEndCurrencyChange"
      />
      <!-- Regular Number Input -->
      <input
        v-else
        type="number"
        v-model="endValue"
        @input="handleNumberRangeFilter"
        @click.stop
        placeholder="Max"
        class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-pr-8 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
      />
      <button
        v-if="endValue !== undefined && endValue !== null"
        @click.stop="clearEndValue"
        type="button"
        class="tw-absolute tw-right-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
        title="Clear maximum value"
      >
        <svg class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import type { Header } from '@tanstack/vue-table';
import FancyCurrencyInput from './FancyCurrencyInput.vue';

const props = defineProps<{
  header: Header<any, any>;
  useCurrency?: boolean;
}>();

const emit = defineEmits<{
  'update:filter': [value: any];
}>();

const startValue = ref<number | undefined>(undefined);
const endValue = ref<number | undefined>(undefined);

// Currency input values (for v-model binding)
const startCurrencyValue = ref<number | undefined>(undefined);
const endCurrencyValue = ref<number | undefined>(undefined);

const handleNumberRangeFilter = () => {
  let filterValue;

  if (startValue.value !== undefined || endValue.value !== undefined) {
    filterValue = {
      start: startValue.value,
      end: endValue.value,
    };
  } else {
    // Clear filter if no values are provided
    filterValue = undefined;
  }

  // Emit the update:filter event instead of calling setFilterValue directly
  emit('update:filter', filterValue);
};

const handleStartCurrencyChange = (value: number | null) => {
  startValue.value = value ?? undefined;
  handleNumberRangeFilter();
};

const handleEndCurrencyChange = (value: number | null) => {
  endValue.value = value ?? undefined;
  handleNumberRangeFilter();
};

const clearStartValue = () => {
  startValue.value = undefined;
  if (props.useCurrency) {
    startCurrencyValue.value = undefined;
  }
  handleNumberRangeFilter();
};

const clearEndValue = () => {
  endValue.value = undefined;
  if (props.useCurrency) {
    endCurrencyValue.value = undefined;
  }
  handleNumberRangeFilter();
};

// Initialize with existing filter values
onMounted(() => {
  const currentFilterValue = props.header.column.getFilterValue();
  if (currentFilterValue && typeof currentFilterValue === 'object') {
    const filterObj = currentFilterValue as any;
    startValue.value = filterObj.start;
    endValue.value = filterObj.end;

    // Set currency input values if using currency
    if (props.useCurrency) {
      startCurrencyValue.value = startValue.value ?? undefined;
      endCurrencyValue.value = endValue.value ?? undefined;
    }
  }
});

// Watch for external filter value changes (when filter is cleared externally)
watch(
  () => props.header.column.getFilterValue(),
  newValue => {
    if (newValue && typeof newValue === 'object') {
      const filterObj = newValue as any;
      startValue.value = filterObj.start;
      endValue.value = filterObj.end;

      if (props.useCurrency) {
        startCurrencyValue.value = startValue.value ?? undefined;
        endCurrencyValue.value = endValue.value ?? undefined;
      }
    } else if (newValue === undefined || newValue === null) {
      startValue.value = undefined;
      endValue.value = undefined;
      if (props.useCurrency) {
        startCurrencyValue.value = undefined;
        endCurrencyValue.value = undefined;
      }
    }
  },
  { immediate: false }
);
</script>
